import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { AdminPagination } from "./AdminPagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  user_activated: "Usuario activado",
  user_suspended: "Usuario suspendido",
  user_edited: "Usuario editado",
  user_archived: "Usuario archivado",
  user_deleted_permanently: "Usuario eliminado definitivamente",
  company_activated: "Empresa activada",
  company_suspended: "Empresa suspendida",
  company_created: "Empresa creada",
  company_edited: "Empresa editada",
  company_archived: "Empresa archivada",
  company_member_added: "Usuario agregado a empresa",
  company_member_role_changed: "Rol de usuario cambiado",
  company_member_status_changed: "Estado de usuario cambiado",
  company_member_removed: "Usuario quitado de empresa",
  company_ownership_transferred: "Propiedad de empresa transferida",
  job_created: "Empleo creado",
  job_edited: "Empleo editado",
  job_paused: "Empleo pausado",
  job_reactivated: "Empleo reactivado",
  job_archived: "Empleo archivado",
  plan_created: "Plan creado",
  plan_edited: "Plan editado",
  plan_commercial_change: "Cambio comercial en plan",
  plan_duplicated: "Plan duplicado",
  plan_activated: "Plan activado",
  plan_deactivated: "Plan desactivado",
  plan_archived: "Plan archivado",
  plan_assigned: "Plan asignado a empresa",
  trial_granted: "Trial otorgado",
  manual_access_granted: "Acceso manual otorgado",
  access_revoked: "Acceso revocado",
  password_changed: "Contraseña cambiada",
  password_reset_completed: "Contraseña restablecida",
};

export default function AdminAuditPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entityFilter, setEntityFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit`, {
        headers: getAdminAuthHeaders(),
        params: { entity_type: entityFilter || undefined, page, limit },
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [entityFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [entityFilter]);

  return (
    <div data-testid="admin-audit-page">
      <h1 className="text-xl font-bold text-slate-900 mb-1">Auditoría</h1>
      <p className="text-sm text-slate-500 mb-4">Registro de acciones administrativas (solo lectura)</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="admin-audit-entity-filter" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todas las entidades</option>
          <option value="user">Usuarios</option>
          <option value="company">Empresas</option>
          <option value="job">Empleos</option>
          <option value="plan">Planes</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="admin-audit-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Admin</th>
              <th className="text-left px-4 py-3">Acción</th>
              <th className="text-left px-4 py-3">Entidad</th>
              <th className="text-left px-4 py-3">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.log_id} data-testid={`admin-audit-row-${log.log_id}`} className="border-t border-slate-100">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-700">{log.admin_email}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{ACTION_LABELS[log.action] || log.action}</td>
                <td className="px-4 py-3 text-slate-600">{log.entity_type} · {log.entity_id}</td>
                <td className="px-4 py-3 text-slate-500 text-xs max-w-[220px] truncate">{JSON.stringify(log.details || {})}</td>
              </tr>
            ))}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-500 text-sm">Sin registros todavía</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}
      </div>

      <AdminPagination page={page} total={total} limit={limit} onPageChange={setPage} testidPrefix="admin-audit" />
    </div>
  );
}
